"use client";

import { useId, useState, type FormEvent } from "react";

import { CcButton } from "@/components/ui/CcButton";

import type { AiKollenEmailCopy } from "./ai-kollen-copy";
import { readAiKollenUtm, trackAiKollenEvent } from "./ai-kollen-track";

type CcAkEmailFormProps = {
  copy: AiKollenEmailCopy;
  privacyHref: string;
  /** Footer Odysseus band: visible labels and light-on-dark field styles. */
  layout?: "page" | "closing";
  className?: string;
};

type Status = "idle" | "sending" | "sent" | "error";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emailDomain(email: string): string {
  return email.trim().toLowerCase().split("@")[1] ?? "";
}

function toWebsite(value: string): string {
  const raw = value.trim();
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

/**
 * Fallback for visitors who don't book: one email field. The company is read
 * from the email domain; with a Gmail/Hotmail-style address we ask for the
 * website too, since we can't run the questions without knowing the company.
 * Sent through the same lead route as the contact form.
 */
export function CcAkEmailForm({
  copy,
  privacyHref,
  layout = "page",
  className = "",
}: CcAkEmailFormProps) {
  const isClosing = layout === "closing";
  const fieldIds = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const emailInputId = `${fieldIds}-email`;
  const websiteInputId = `${fieldIds}-website`;
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  const domain = emailDomain(email);
  const needsWebsite = EMAIL_PATTERN.test(email.trim()) && copy.freemailDomains.includes(domain);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "sending") return;
    const address = email.trim();
    if (!EMAIL_PATTERN.test(address) || (needsWebsite && !website.trim())) return;

    const companyWebsite = toWebsite(needsWebsite ? website : domain);
    let company = domain;
    try {
      company = new URL(companyWebsite).hostname.replace(/^www\./, "");
    } catch {
      // Keep the email domain.
    }
    const utm = readAiKollenUtm();
    const utmLine = Object.entries(utm)
      .map(([key, value]) => `${key}=${value}`)
      .join(", ");

    setStatus("sending");
    try {
      const response = await fetch("/api/proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "formSubmission",
          name: address.split("@")[0],
          email: address,
          company,
          website: companyWebsite,
          body: [
            "AI-kollen: wants the measurement by email (no meeting booked).",
            `Company website: ${companyWebsite}`,
            utmLine ? `Campaign: ${utmLine}` : "Campaign: none",
          ].join("\n"),
          services: ["ai-visibility"],
          language: navigator.language,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          source: window.location.href,
          journey: null,
          ourCompany: "captivateclick",
        }),
      });
      const receipt = await response.json().catch(() => null);
      if (!response.ok || receipt?.received !== true || typeof receipt.taskId !== "string" || !receipt.taskId) {
        throw new Error("Submission was not saved");
      }
      setStatus("sent");
      trackAiKollenEvent("ai_kollen_email");
    } catch {
      setStatus("error");
    }
  }

  if (status === "sent") {
    return (
      <p
        className={`cc-ak-email__success${isClosing ? " cc-ak-email__success--closing" : ""}`}
        role="status"
      >
        {copy.success}
      </p>
    );
  }

  if (isClosing) {
    return (
      <form
        data-analytics-form="ai_kollen_email"
        className={["cc-seo-cta__form", "cc-home-closing-cta__seo-form", className]
          .filter(Boolean)
          .join(" ")}
        onSubmit={handleSubmit}
        noValidate
      >
        <label className="cc-seo-cta__label" htmlFor={emailInputId}>
          {copy.emailLabel}
        </label>
        <div className="cc-seo-cta__row">
          <input
            id={emailInputId}
            className="cc-seo-cta__input"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            required
            placeholder={copy.emailPlaceholder}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <button
            type="submit"
            className="cc-seo-cta__button cc-seo-cta__button--brand"
            disabled={status === "sending"}
          >
            {status === "sending" ? copy.sending : copy.submit}
          </button>
        </div>
        {needsWebsite ? (
          <>
            <label className="cc-seo-cta__label" htmlFor={websiteInputId}>
              {copy.websiteLabel}
            </label>
            <div className="cc-seo-cta__row">
              <input
                id={websiteInputId}
                className="cc-seo-cta__input"
                type="text"
                name="website"
                autoComplete="url"
                inputMode="url"
                required
                placeholder={copy.websitePlaceholder}
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
              />
            </div>
          </>
        ) : null}
        <p
          className={`cc-seo-cta__error${status === "error" ? " cc-seo-cta__error--visible" : ""}`}
          role={status === "error" ? "alert" : undefined}
          aria-hidden={status === "error" ? undefined : true}
        >
          {copy.error}
        </p>
        <p className="cc-seo-cta__note">
          {copy.privacy}{" "}
          <a href={privacyHref}>{copy.privacyLink}</a>
        </p>
      </form>
    );
  }

  return (
    <form
        data-analytics-form="ai_kollen_email"
      className={["cc-ak-email__form", className].filter(Boolean).join(" ")}
      onSubmit={handleSubmit}
    >
      <label className="cc-ak-email__field">
        <span className="cc-ak-sr">{copy.emailLabel}</span>
        <input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          required
          placeholder={copy.emailPlaceholder}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      {needsWebsite ? (
        <label className="cc-ak-email__field cc-ak-email__field--website">
          <span className="cc-ak-email__hint">{copy.websiteLabel}</span>
          <input
            type="text"
            name="website"
            autoComplete="url"
            inputMode="url"
            required
            placeholder={copy.websitePlaceholder}
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </label>
      ) : null}
      <CcButton
        type="submit"
        variant="dark"
        label={status === "sending" ? copy.sending : copy.submit}
        expand
        expandCompact
        loading={status === "sending"}
        className="cc-ak-email__submit"
      />
      {status === "error" ? (
        <p className="cc-ak-email__error" role="alert">
          {copy.error}
        </p>
      ) : null}
      <p className="cc-ak-email__privacy">
        {copy.privacy} <a href={privacyHref}>{copy.privacyLink}</a>
      </p>
    </form>
  );
}
